import React from "react";
import axios, { AxiosResponse } from "axios";
import { enqueueSnackbar } from "notistack";
import { AppContext, User } from "../../../../context/appContext";
import { endpoints } from "../../../../endpoints/endpoints";
import AdminListProps from "../interface/adminListProps";
import AdminProps from "../interface/adminProps";

export const AdminListContext = React.createContext<any>({});

const AdminListProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element => {
  const {
    user,
    checkSession,
  }: {
    user: User;
    checkSession: (response: AxiosResponse) => void;
  } = React.useContext(AppContext);

  const [adminList, setAdminList] = React.useState<AdminListProps>({
    loading: false,
    fetched: false,
    data: [] as AdminProps[],
  });

  const [search, setSearch] = React.useState<string>("");
  const [addAdmin, setAddAdmin] = React.useState<boolean>(false);
  const [revokeAdmin, setRevokeAdmin] = React.useState<AdminProps | null>(null);

  const fetchAdminList = React.useCallback(async (): Promise<void> => {
    setAdminList((currentList) => ({ ...currentList, loading: true }));

    await axios
      .post(endpoints.admin.getAdminList, {
        sessionId: user.sessionId,
      })
      .then((response) => {
        checkSession(response);

        if (response.data.meta.status !== "success") {
          setAdminList({ loading: false, fetched: true, data: [] });

          enqueueSnackbar("Fehler beim Laden der Admins!", {
            variant: "error",
          });
          return;
        }

        setAdminList({
          loading: false,
          fetched: true,
          data: response.data.data,
        });
      })
      .catch((error) => {
        console.error(error);
        setAdminList({ loading: false, fetched: true, data: [] });

        enqueueSnackbar("Fehler beim Laden der Admins!", {
          variant: "error",
        });
      });
  }, [checkSession, user.sessionId]);

  const makeUserToAdmin = React.useCallback(
    async (userId: number): Promise<void> => {
      await axios
        .post(endpoints.admin.makeUserToAdmin, {
          sessionId: user.sessionId,
          userId: userId,
        })
        .then((response) => {
          checkSession(response);

          if (response.data.meta.status !== "success") {
            enqueueSnackbar(
              response.data.meta.message || "Fehler beim Vergeben der Admin-Rechte!",
              { variant: "error" }
            );
            return;
          }

          setAddAdmin(false);
          fetchAdminList();

          enqueueSnackbar("Admin-Rechte erfolgreich vergeben!", {
            variant: "success",
          });
        })
        .catch((error) => {
          console.error(error);
          enqueueSnackbar("Fehler beim Vergeben der Admin-Rechte!", {
            variant: "error",
          });
        });
    },
    [checkSession, fetchAdminList, user.sessionId]
  );

  const removeAdminRights = React.useCallback(
    async (userId: number): Promise<void> => {
      await axios
        .post(endpoints.admin.removeAdminRights, {
          sessionId: user.sessionId,
          userId: userId,
        })
        .then((response) => {
          checkSession(response);

          if (response.data.meta.status !== "success") {
            enqueueSnackbar(
              response.data.meta.message || "Fehler beim Entziehen der Admin-Rechte!",
              { variant: "error" }
            );
            return;
          }

          setRevokeAdmin(null);
          fetchAdminList();

          enqueueSnackbar("Admin-Rechte erfolgreich entzogen!", {
            variant: "success",
          });
        })
        .catch((error) => {
          console.error(error);
          enqueueSnackbar("Fehler beim Entziehen der Admin-Rechte!", {
            variant: "error",
          });
        });
    },
    [checkSession, fetchAdminList, user.sessionId]
  );

  React.useEffect(() => {
    if (adminList.fetched) return;
    if (adminList.loading) return;

    fetchAdminList();
  }, [adminList.fetched, adminList.loading, fetchAdminList]);

  const value: {
    adminList: AdminListProps;
    search: string;
    setSearch: React.Dispatch<React.SetStateAction<string>>;
    addAdmin: boolean;
    setAddAdmin: React.Dispatch<React.SetStateAction<boolean>>;
    revokeAdmin: AdminProps | null;
    setRevokeAdmin: React.Dispatch<React.SetStateAction<AdminProps | null>>;
    fetchAdminList: () => Promise<void>;
    makeUserToAdmin: (userId: number) => Promise<void>;
    removeAdminRights: (userId: number) => Promise<void>;
  } = React.useMemo(
    () => ({
      adminList,
      search,
      setSearch,
      addAdmin,
      setAddAdmin,
      revokeAdmin,
      setRevokeAdmin,
      fetchAdminList,
      makeUserToAdmin,
      removeAdminRights,
    }),
    [
      adminList,
      search,
      addAdmin,
      revokeAdmin,
      fetchAdminList,
      makeUserToAdmin,
      removeAdminRights,
    ]
  );

  return (
    <AdminListContext.Provider value={value}>
      {children}
    </AdminListContext.Provider>
  );
};

export default AdminListProvider;
